import { createContext, useContext } from 'react'

import type { ControllerConfigVersion, ControllerConfigVersionUpdate } from '@/features/controller/configuration/types'

/**
 * Neutral, feature-agnostic config-file types and context shared by every surface that edits a
 * versioned configuration file (controller config pages and the resource-scoped resource.json page).
 *
 * Both consumers MUST depend on this module rather than on a feature-specific context type, so that a
 * change to the contract breaks every consumer at compile time.
 */
export type ConfigurationVersion = ControllerConfigVersion
export type ConfigurationVersionUpdate = ControllerConfigVersionUpdate

export type ConfigurationFileContextType = {
  isLatestVersionFetched: boolean
  isLatestVersionLoading: boolean
  isUpdatePending: boolean
  latestVersion: ConfigurationVersion | null
  updateAndInvalidate: (update: ConfigurationVersionUpdate) => Promise<void>
}

export const ConfigurationFileContext = createContext<ConfigurationFileContextType | null>(null)

export function useConfigurationFileContext() {
  const context = useContext(ConfigurationFileContext)
  if (!context) {
    throw new Error('useConfigurationFileContext must be used within a configuration file provider')
  }
  return context
}
